import {
  ActionIcon,
  Badge,
  Box,
  Button,
  FileButton,
  Flex,
  Grid,
  Loader,
  LoadingOverlay,
  Menu,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { useDebouncedState, useDisclosure, useHotkeys } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import {
  IconCaretDown,
  IconDownload,
  IconEdit,
  IconFileImport,
  IconFileTypeCsv,
  IconPlus,
  IconSearch,
  IconTrash,
} from "@tabler/icons-react";
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_RowSelectionState,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useDeferredValue, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { repositoryPos } from "../../../../_base/_const/_constVar";
import { NotificationExtension } from "../../../../_base/extension/NotificationExtension";
import { BASE_API_POS } from "../../../../config";
import { _localization } from "../../../../config/location";
import { MessageResponse } from "../../../../model/MessageResponse";
import CUDDistanceFare from "./CUDDistanceFare";
import DeleteDistanceFare from "./DeleteDistanceFare";

type ColumnPinningType = {
  left?: string[];
  right?: string[];
};

const DistanceFare = () => {
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [data, setData] = useState<any[]>([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [height, setHeight] = useState(0);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });
  const rowSelectionDeferred =
    useDeferredValue<MRT_RowSelectionState>(rowSelection);
  const [selectIds, setSelectIds] = useState<string[]>([]);
  const [visible, { toggle, open, close }] = useDisclosure(false);
  const resetRef = useRef<() => void>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const _searchJson = sessionStorage.getItem(String(location.pathname))
    ? JSON.parse(sessionStorage.getItem(String(location.pathname)) ?? "")
    : null;
  const checkedPinSearch = localStorage.getItem("checkedPinSearch") == "true";
  const [search, setSearch] = useState<any>({
    Code: _searchJson && checkedPinSearch ? _searchJson?.Code : "",
  });
  const [keySearch, setKeySearch] = useDebouncedState<string>(
    _searchJson && checkedPinSearch ? _searchJson?.keySearch : "",
    300,
  );

  const columns = React.useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "code",
        header: "Mã",
        Cell: ({ renderedCellValue }) =>
          renderedCellValue ? (
            <Badge variant="dot" color="green">
              {renderedCellValue}
            </Badge>
          ) : (
            <></>
          ),
      },
      {
        accessorKey: "distance",
        header: "Quãng đường tính lương",
        Cell: ({ renderedCellValue }) => (
          <Text fw={500} size="12.5px">
            {renderedCellValue ? `${renderedCellValue} km` : ""}
          </Text>
        ),
      },
      {
        accessorKey: "value",
        header: "Lương vận chuyển",
        Cell: ({ renderedCellValue }) => (
          <Text fw={500} size="12.5px">
            {renderedCellValue
              ? `${Number(renderedCellValue).toLocaleString()} VNĐ`
              : ""}
          </Text>
        ),
      },
      {
        accessorKey: "action",
        header: "Thao tác",
        enableSorting: false,
        enableColumnFilter: false,
        size: 70,
        Cell: ({ row }) => (
          <Flex justify={"start"} align={"center"} gap={"xs"}>
            <Tooltip label="Chỉnh sửa">
              <ActionIcon
                variant="light"
                color="orange"
                onClick={() => modalCreateAndEdit(row.original)}
              >
                <IconEdit size={18} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Xóa">
              <ActionIcon
                variant="light"
                color="red"
                onClick={() => modalDelete([Number(row.original.id)])}
              >
                <IconTrash size={18} />
              </ActionIcon>
            </Tooltip>
          </Flex>
        ),
      },
    ],
    [],
  );

  const modalCreateAndEdit = (data?: any) => {
    modals.openConfirmModal({
      title: <Title order={5}>Thiết lập quãng đường tính lương !</Title>,
      size: "20vw",
      children: <CUDDistanceFare data={data} onSuccess={fetchData} />,
      confirmProps: { display: "none" },
      cancelProps: { display: "none" },
    });
  };

  const modalDelete = (listId: number[]) => {
    modals.openConfirmModal({
      title: <Title order={5}>Xóa thiết lập quãng đường tính lương !</Title>,
      size: "auto",
      children: <DeleteDistanceFare listId={listId} onSuccess={fetchData} />,
      confirmProps: { display: "none" },
      cancelProps: { display: "none" },
    });
  };

  const fetchData = async () => {
    setIsLoading(true);
    setIsRefetching(true);
    setIsError(false);
    try {
      let url = `/api/v1/DistanceFare/get-list?Skip=${
        pagination.pageIndex * pagination.pageSize
      }&Take=${pagination.pageSize}`;

      const params = new URLSearchParams();
      Object.entries(search).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          value.forEach((item) => params.append(key, String(item)));
        } else if (typeof value === "string" && value.length > 0) {
          params.append(key, value);
        }
      });

      if (keySearch.length > 0) {
        url += `&KeySearch=${keySearch?.trim()}`;
      }

      url += `&${params?.toString()}`;

      const res = await repositoryPos.get<MessageResponse<any>>(url);

      if (res && res.success) {
        const result = res.data;
        setData(result);
        setRowCount(res.totalCount);
        table.resetRowSelection();
      } else {
        setData([]);
      }
    } catch (error) {
      setData([]);
      setIsError(true);
      console.error(error);
      return;
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
      sessionStorage.setItem(
        String(location.pathname),
        JSON.stringify({
          ...search,
          keySearch: keySearch,
        }),
      );
    }
  };

  const exportDataExcel = async () => {
    open();
    try {
      let url = `?Skip=${pagination?.pageIndex}&Take=${pagination.pageSize}`;

      const params = new URLSearchParams();
      Object.entries(search).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          value.forEach((item) => params.append(key, String(item)));
        } else if (typeof value === "string" && value.length > 0) {
          params.append(key, value);
        }
      });

      if (keySearch.length > 0) {
        url += `&KeySearch=${keySearch?.trim()}`;
      }

      url += `&${params.toString()}`;

      const baseUrl = `/api/v1/DistanceFare/export-excel${url}`;

      const _url = BASE_API_POS + baseUrl;
      const token = localStorage.getItem("token");

      const response = await fetch(_url, {
        method: "GET",
        headers: {
          "Content-Type": "application/vnd.ms-excel",
          Authorization: `Bearer ${token?.replace(`"`, "").replace(`"`, "")}`,
        },
      });

      if (!response.ok) {
        NotificationExtension.Fails("Export Excel thất bại !");
      } else {
        const blob = await response.blob();
        const urlss = window.URL.createObjectURL(blob);
        const _name = new Date().toISOString().slice(0, 10);
        let filename = "danh_sach_tinh_luong_theo_khoang_cach.xlsx";
        const link = document.createElement("a");
        link.href = urlss;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        NotificationExtension.Success("Export Excel thành công !");
      }
    } catch (error) {
      console.log(error);
    } finally {
      close();
    }
  };

  const handleDownloadFileSample = async () => {
    open();
    try {
      const token = localStorage.getItem("token");
      await fetch(
        `${BASE_API_POS}/api/v1/DistanceFare/get-import-excel-distance-fare-template`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token?.replace(/"/g, "")}`,
            "Content-Type": "application/vnd.ms-excel",
          },
        },
      )
        .then((response) => {
          if (response.ok) {
            return response.blob();
          } else {
            throw new Error("Failed to fetch the resource");
          }
        })
        .then((blob) => {
          const url = window.URL.createObjectURL(blob);
          let filename = "mau_cau_hinh_tinh_luong_theo_khoang_cach.xlsx";
          const link = document.createElement("a");
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          NotificationExtension.Success("Lấy file mẫu thành công !");
        })
        .catch((error) => {
          console.error("Error:", error);
          NotificationExtension.Fails("Lấy file mẫu thất bại !");
        });
    } catch (error) {
      console.log("Error downloading template:", error);
      NotificationExtension.Fails("Tải file mẫu thất bại");
    } finally {
      close(); // Đóng loading overlay sau khi tải xong
    }
  };

  const uploadFile = async (e: File) => {
    if (!e) {
      NotificationExtension.Fails("File rỗng hoặc chưa chọn file !");
      return;
    }
    open();
    // await new Promise((resolve) => setTimeout(resolve, 2000));
    const formData = new FormData();
    formData.append("file", e);

    try {
      const resSaveFile = await repositoryPos.post<MessageResponse<any>>(
        "/api/v1/DistanceFare/import-excel",
        formData,
      );
      if (resSaveFile && resSaveFile.success) {
        resetRef.current?.();
        NotificationExtension.Success("Import file thành công !");
        setTimeout(() => {
          fetchData();
        }, 1000);
      } else {
        NotificationExtension.Fails("Import file thất bại !");
        resetRef.current?.();
      }
    } catch (error) {
      console.log(error);
    } finally {
      close();
      resetRef.current?.();
    }
  };

  useEffect(() => {
    fetchData();
  }, [pagination?.pageIndex, pagination?.pageSize]);

  const getColumnPinningStorage = () => {
    const columnPinningStorage = localStorage.getItem(
      "columnPinningDistanceFare",
    );
    return columnPinningStorage
      ? JSON.parse(columnPinningStorage)
      : {
          left: [],
          right: [],
        }; // Giá trị mặc định
  };

  // Lưu columnPinning vào state
  const [columnPinning, setColumnPinning] = useState<ColumnPinningType>(
    getColumnPinningStorage(),
  );
  // Hàm lưu vào localStorage
  useEffect(() => {
    localStorage.setItem(
      "columnPinningDistanceFare",
      JSON.stringify(columnPinning),
    );
  }, [columnPinning]);

  useEffect(() => {
    //   startTransition(() => {
    const valuesList = Object.keys(rowSelectionDeferred);
    setSelectIds(valuesList);
    if (valuesList.length < 1) setSelectIds([]);
    else {
      const valuesList = Object.keys(rowSelectionDeferred);
      setSelectIds(valuesList);
    }
    //  });
  }, [rowSelectionDeferred]);

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      setHeight(window.innerHeight - (250 + headerHeight));
    };

    handleResize(); // Set initial height
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const table = useMantineReactTable({
    columns,
    data,
    enableColumnResizing: false,
    positionToolbarAlertBanner: "bottom",
    enableRowNumbers: true,
    //add custom action buttons to top-left of top toolbar
    enableTopToolbar: false,
    mantineTopToolbarProps: {
      style: {
        borderBottom: "3px solid rgba(128, 128, 128, 0.5)",
        marginBottom: 5,
      },
    },
    localization: _localization,
    enableRowSelection: true,
    getRowId: (row) => row.id?.toString(),
    initialState: {
      showColumnFilters: false,
      columnPinning: {
        // left: ["mrt-row-numbers", "maVt"],
        // right: ["action"],
        ...columnPinning,
      },
      columnVisibility: { id: true, phone: false, fax: false },
      density: "xs",
    },
    mantineTableContainerProps: {
      style: { maxHeight: height, minHeight: height },
    },
    enableStickyHeader: true,
    manualPagination: true,
    onColumnPinningChange: (newState) => {
      setColumnPinning(newState);
    },
    onRowSelectionChange: setRowSelection,
    rowCount: rowCount,
    onPaginationChange: setPagination,
    mantineTableBodyCellProps: ({ row }) => ({
      style: {
        fontWeight: "500", //conditional styling
        fontSize: "12.5px",
        padding: "5px 15px",
      },
    }),
    state: {
      isLoading,
      pagination: pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      showSkeletons: isLoading, //loading for the first time with no data
      rowSelection,
      columnPinning,
    },
    mantineToolbarAlertBannerProps: isError
      ? { color: "red", children: "Lỗi tải dữ liệu !" }
      : undefined,
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      rowsPerPageOptions: ["20", "50", "100"],
    },
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
    },
    columnFilterDisplayMode: "popover",
    mantineTableBodyRowProps: ({ row }) => ({
      style: { cursor: "pointer" },
    }),
  });

  useHotkeys([
    [
      "Enter",
      () => {
        fetchData();
      },
    ],
  ]);

  return (
    <>
      <div
        ref={headerRef}
        style={{
          width: "100%",
        }}
      >
        <LoadingOverlay
          visible={visible}
          zIndex={1000}
          overlayProps={{ radius: "sm", blur: 5 }}
          loaderProps={{
            children: (
              <Flex direction={"column"} align="center" gap="xs">
                <Loader type="dots" size={35}></Loader>
                {/* <Title size={15}> Đang upload tài liệu...</Title> */}
                <Title size={15}> Đang xử lý....</Title>
              </Flex>
            ),
          }}
        />
        <Flex justify={"space-between"} align={"center"} w={"100%"}>
          <Grid mt={10} w={"80%"}>
            <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2.5 }}>
              <TextInput
                placeholder="Nhập từ khóa tìm kiếm"
                defaultValue={keySearch}
                onChange={(e) => setKeySearch(e.currentTarget.value)}
                leftSection={<IconSearch color="#15aabf" />}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    fetchData();
                  }
                }}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
              <Button
                onClick={() => fetchData()}
                leftSection={<IconSearch size={14} />}
              >
                Tìm kiếm
              </Button>
            </Grid.Col>
          </Grid>
          <Flex gap={"md"}>
            <Button
              leftSection={<IconTrash size={16} />}
              color="red"
              disabled={selectIds.length === 0}
              onClick={() => modalDelete(selectIds.map((i: any) => Number(i)))}
            >
              Xóa (Các mục đã chọn)
            </Button>
            <Button
              leftSection={<IconPlus size={16} />}
              color="green"
              onClick={() => modalCreateAndEdit()}
            >
              Thêm mới
            </Button>
            <Menu>
              <Menu.Target>
                <Button rightSection={<IconCaretDown size={14} />}>
                  Excel
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Flex direction={"column"} gap={2.5}>
                  <Button
                    leftSection={<IconDownload size={14} />}
                    onClick={() => handleDownloadFileSample()}
                  >
                    Tải file mẫu
                  </Button>
                  <Button
                    leftSection={<IconFileTypeCsv size={14} />}
                    onClick={() => exportDataExcel()}
                  >
                    Export Excel
                  </Button>
                  <FileButton
                    resetRef={resetRef}
                    onChange={(e) => {
                      if (e) {
                        if (
                          e &&
                          (e.type ===
                            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
                            e.type === "application/vnd.ms-excel")
                        )
                          uploadFile(e);
                        else
                          NotificationExtension.Fails(
                            "File không đúng định đạng Excel !",
                          );
                      }
                    }}
                  >
                    {(props) => (
                      <Button
                        leftSection={<IconFileImport size={14} />}
                        {...props}
                      >
                        Import Excel
                      </Button>
                    )}
                  </FileButton>
                </Flex>
              </Menu.Dropdown>
            </Menu>
          </Flex>
        </Flex>
      </div>
      <Box mt={10}>
        <MantineReactTable table={table} />
      </Box>
    </>
  );
};

export default DistanceFare;
